import React, { useEffect, useState } from 'react';
import { Beaker, Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../App';
import { cn } from '../../lib/utils';
import QuickReviewPanel from './QuickReviewPanel';

const HIDE_ROUTES = ['/', '/landing', '/features', '/for-ceos', '/pricing', '/auth/callback', '/capture'];

/**
 * Floating "Test this page" widget. Super-Admin only.
 *
 * v2 (Quick Review) — clicking the button opens a slide-over QuickReviewPanel
 * with plain-English test cards. Pass / Fail / Pass-with-note. Submitted
 * verdicts become structured TestFindings + Mini-PRDs ready for Change Order
 * aggregation.
 *
 * The old element-by-element popover is gone — power users still have the full
 * Testing Lab page (/testing-lab) for detailed runs with screen capture, voice,
 * etc.
 */
export const TestThisNowWidget = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isAdmin, setIsAdmin] = useState(false);
  const [open, setOpen] = useState(false);
  const [pageTitle, setPageTitle] = useState('');

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    fetch('/api/testing-lab/super-admin/me', { credentials: 'include' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setIsAdmin(!!d?.is_super_admin))
      .catch(() => setIsAdmin(false));
  }, [user]);

  useEffect(() => {
    setPageTitle(document.title || location.pathname);
  }, [location.pathname]);

  if (!user || !isAdmin) return null;
  if (HIDE_ROUTES.includes(location.pathname)) return null;
  if (location.pathname.startsWith('/testing-lab')) return null;

  const route = location.pathname + location.search;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={cn(
          'fixed z-30 right-4 bottom-24 group flex items-center gap-2 px-3 py-2 rounded-full',
          'bg-gradient-to-br from-violet-600 to-indigo-700 text-white shadow-lg shadow-violet-500/30',
          'hover:shadow-xl hover:shadow-violet-500/50 hover:scale-[1.03] active:scale-95',
          'orka-transition-colors border border-violet-400/30',
        )}
        title="Quick review of this page"
        data-testid="test-this-now-btn"
      >
        <span className="w-6 h-6 rounded-full bg-white/15 flex items-center justify-center">
          <Beaker className="w-3.5 h-3.5" />
        </span>
        <span className="text-xs font-medium pr-1">Test this page</span>
      </button>

      <QuickReviewPanel
        open={open}
        onClose={() => setOpen(false)}
        route={route}
        pageTitle={pageTitle}
      />
    </>
  );
};

export default TestThisNowWidget;
